import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./Accordion.module.css";

const AccordionContext = createContext(null);
const AccordionItemContext = createContext(null);

function cx(...parts) {
  return parts.filter(Boolean).join(" ");
}

function normalizeSingleValue(value) {
  return typeof value === "string" ? value : null;
}

function normalizeMultipleValue(value) {
  return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
}

function getResolvedKeepMounted(keepMounted, unmountOnExit) {
  if (typeof keepMounted === "boolean") return keepMounted;
  if (typeof unmountOnExit === "boolean") return !unmountOnExit;
  return false;
}

function getNextValue({ mode, currentValue, itemValue }) {
  if (mode === "multiple") {
    const current = normalizeMultipleValue(currentValue);
    return current.includes(itemValue)
      ? current.filter((value) => value !== itemValue)
      : [...current, itemValue];
  }

  const current = normalizeSingleValue(currentValue);
  return current === itemValue ? null : itemValue;
}

const Accordion = forwardRef(function Accordion(
  {
    mode = "single",
    defaultValue,
    value,
    onValueChange,
    className,
    transitionDuration = 220,
    keepMounted,
    unmountOnExit,
    children,
    ...rest
  },
  ref
) {
  const isControlled = value !== undefined;

  const initialValue = useMemo(() => {
    if (mode === "multiple") {
      if (defaultValue === undefined) return [];
      return normalizeMultipleValue(defaultValue);
    }
    if (defaultValue === undefined) return null;
    return normalizeSingleValue(defaultValue);
  }, [defaultValue, mode]);

  const [internalValue, setInternalValue] = useState(initialValue);

  const currentValue = isControlled ? value : internalValue;
  const resolvedKeepMounted = getResolvedKeepMounted(keepMounted, unmountOnExit);

  const headerRegistryRef = useRef(new Map());

  const registerHeader = (itemValue, element) => {
    if (!itemValue || !element) return;
    headerRegistryRef.current.set(itemValue, element);
  };

  const unregisterHeader = (itemValue, element) => {
    const current = headerRegistryRef.current.get(itemValue);
    if (current && current === element) {
      headerRegistryRef.current.delete(itemValue);
    }
  };

  const getOrderedEnabledHeaders = () => {
    return Array.from(headerRegistryRef.current.entries())
      .filter(([, element]) => element && !element.disabled)
      .sort((a, b) => {
        const pos = a[1].compareDocumentPosition(b[1]);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
  };

  const focusHeaderByDirection = (itemValue, direction) => {
    const headers = getOrderedEnabledHeaders();
    if (!headers.length) return;

    const currentIndex = headers.findIndex(([value]) => value === itemValue);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    if (direction === "next") {
      nextIndex = (currentIndex + 1) % headers.length;
    } else if (direction === "prev") {
      nextIndex = (currentIndex - 1 + headers.length) % headers.length;
    } else if (direction === "first") {
      nextIndex = 0;
    } else if (direction === "last") {
      nextIndex = headers.length - 1;
    }

    headers[nextIndex]?.[1]?.focus();
  };

  const isItemOpen = (itemValue) => {
    if (mode === "multiple") {
      return normalizeMultipleValue(currentValue).includes(itemValue);
    }
    return normalizeSingleValue(currentValue) === itemValue;
  };

  const toggleItem = (itemValue) => {
    const nextValue = getNextValue({
      mode,
      currentValue,
      itemValue,
    });

    if (!isControlled) {
      setInternalValue(nextValue);
    }

    if (typeof onValueChange === "function") {
      onValueChange(nextValue);
    }
  };

  const contextValue = useMemo(
    () => ({
      mode,
      value: currentValue,
      isItemOpen,
      toggleItem,
      transitionDuration,
      keepMounted: resolvedKeepMounted,
      registerHeader,
      unregisterHeader,
      focusHeaderByDirection,
    }),
    [mode, currentValue, transitionDuration, resolvedKeepMounted]
  );

  return (
    <AccordionContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cx(styles.root, className)}
        data-mode={mode}
        {...rest}
      >
        {children}
      </div>
    </AccordionContext.Provider>
  );
});

const AccordionItem = forwardRef(function AccordionItem(
  { value, disabled = false, className, children, ...rest },
  ref
) {
  const accordion = useContext(AccordionContext);

  if (!accordion) {
    throw new Error("AccordionItem must be used inside Accordion.");
  }

  if (typeof value !== "string" || !value.trim()) {
    throw new Error('AccordionItem requires a non-empty string "value" prop.');
  }

  const reactId = useId();
  const safeValue = value.replace(/[^a-zA-Z0-9_-]/g, "-");
  const baseId = `accordion-${safeValue}-${reactId.replace(/:/g, "")}`;
  const open = accordion.isItemOpen(value);

  const itemContextValue = useMemo(
    () => ({
      value,
      disabled,
      open,
      baseId,
    }),
    [value, disabled, open, baseId]
  );

  return (
    <AccordionItemContext.Provider value={itemContextValue}>
      <div
        ref={ref}
        className={cx(
          styles.item,
          open && styles.itemOpen,
          disabled && styles.itemDisabled,
          className
        )}
        data-state={open ? "open" : "closed"}
        data-disabled={disabled ? "true" : "false"}
        {...rest}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
});

const AccordionHeader = forwardRef(function AccordionHeader(
  {
    className,
    children,
    icon,
    iconPosition = "end",
    hideIcon = false,
    ...rest
  },
  ref
) {
  const accordion = useContext(AccordionContext);
  const item = useContext(AccordionItemContext);
  const internalRef = useRef(null);

  if (!accordion || !item) {
    throw new Error("AccordionHeader must be used inside AccordionItem.");
  }

  const { value, disabled, open, baseId } = item;
  const headerId = `${baseId}-trigger`;
  const panelId = `${baseId}-panel`;

  useEffect(() => {
    const element = internalRef.current;
    if (!element) return;

    accordion.registerHeader(value, element);
    return () => {
      accordion.unregisterHeader(value, element);
    };
  }, [accordion, value]);

  const setRefs = (node) => {
    internalRef.current = node;

    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    accordion.toggleItem(value);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        accordion.focusHeaderByDirection(value, "next");
        break;
      case "ArrowUp":
        event.preventDefault();
        accordion.focusHeaderByDirection(value, "prev");
        break;
      case "Home":
        event.preventDefault();
        accordion.focusHeaderByDirection(value, "first");
        break;
      case "End":
        event.preventDefault();
        accordion.focusHeaderByDirection(value, "last");
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        accordion.toggleItem(value);
        break;
      default:
        break;
    }
  };

  const defaultIcon = (
    <span
      className={cx(styles.icon, open && styles.iconOpen)}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6 8 10 12 14 8" />
      </svg>
    </span>
  );

  return (
    <button
      ref={setRefs}
      type="button"
      id={headerId}
      className={cx(
        styles.header,
        styles[`icon${iconPosition === "start" ? "Start" : "End"}`],
        className
      )}
      aria-expanded={open}
      aria-controls={panelId}
      disabled={disabled}
      data-state={open ? "open" : "closed"}
      data-disabled={disabled ? "true" : "false"}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {!hideIcon && iconPosition === "start" && (icon || defaultIcon)}

      <span className={styles.headerContent}>{children}</span>

      {!hideIcon && iconPosition === "end" && (icon || defaultIcon)}
    </button>
  );
});

const AccordionPanel = forwardRef(function AccordionPanel(
  { className, children, ...rest },
  ref
) {
  const accordion = useContext(AccordionContext);
  const item = useContext(AccordionItemContext);

  if (!accordion || !item) {
    throw new Error("AccordionPanel must be used inside AccordionItem.");
  }

  const { open, baseId } = item;
  const panelId = `${baseId}-panel`;
  const headerId = `${baseId}-trigger`;
  const shouldRender = accordion.keepMounted || open;

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={ref}
      id={panelId}
      role="region"
      aria-labelledby={headerId}
      className={cx(styles.panelOuter, open && styles.panelOuterOpen, className)}
      data-state={open ? "open" : "closed"}
      style={{ "--accordion-duration": `${accordion.transitionDuration}ms` }}
      hidden={!open}
      {...rest}
    >
      <div className={styles.panelInner}>{children}</div>
    </div>
  );
});

export default Accordion;
export { Accordion, AccordionItem, AccordionHeader, AccordionPanel };